import { api } from '../../core/network/api';

/**
 * Thin REST wrappers for the Rankings screen. `fetchLeaderboard` hits core's
 * generic leaderboard API (backend/src/core/leaderboard) directly — shape
 * taken verbatim from core/leaderboard/leaderboard.types.ts — and takes a
 * `category` param passed through rather than hardcoded, even though
 * `colony-power` is the only one this game registers (game/leaderboard-feed),
 * so a second category can be added later without touching this module.
 * `fetchPersonalRank` and `fetchWorldRanking` instead go through
 * game/leaderboard-feed's own endpoints — see each one's own doc comment for why.
 */

/** One row of `GET /leaderboard/:category`. */
export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string | null;
  value: number;
}

/**
 * `GET /rankings/me` — the caller's own `'colony-power'` standing, WITH
 * identity (`userId`/`username`) attached. Backed by
 * `LeaderboardFeedService.getPersonalColonyPower` (game/leaderboard-feed),
 * not core's own `GET /leaderboard/:category/me` directly: core's shape is
 * generic over any `User` field and carries no display identity, which is
 * exactly what the Rankings screen needs to render this as a real row when
 * the caller ranks outside the top N `fetchLeaderboard`/`fetchWorldRanking`
 * return.
 */
export interface PersonalRank {
  rank: number;
  value: number;
  total: number;
  userId: string;
  username: string | null;
}

/** Public — no auth needed. `limit` is clamped server-side to 100. */
export function fetchLeaderboard(category: string, limit = 20, offset = 0): Promise<LeaderboardEntry[]> {
  return api.get<LeaderboardEntry[]>(`/leaderboard/${category}?limit=${limit}&offset=${offset}`);
}

/** Needs auth — resolves the current JWT's own standing on the `'colony-power'` board. */
export function fetchPersonalRank(): Promise<PersonalRank> {
  return api.get<PersonalRank>('/rankings/me');
}

/**
 * One row of `GET /rankings/world/:worldId` — the game-side per-world board
 * (backend/src/game/leaderboard-feed), not core's.
 *
 * It ranks COLONIES, not players, which is why it carries a colony name
 * alongside the owner: core's board sums a player's power across every world
 * they hold, so reusing it per world would credit a colony here with power
 * earned somewhere else entirely.
 */
export interface WorldRankingEntry {
  rank: number;
  colonyId: string;
  colonyName: string;
  userId: string;
  username: string | null;
  value: number;
}

/**
 * Needs auth. `limit` is clamped server-side to 100. The caller's own colony
 * is resolved from the JWT server-side (`LeaderboardFeedService.getWorldRanking`)
 * and, if it ranks outside `limit`, appended as one extra trailing entry
 * carrying its TRUE rank — so the array may come back as `limit + 1` long.
 * See `leaderboard-screen.ts`'s `withPlayerRow` for how that's rendered.
 */
export function fetchWorldRanking(worldId: string, limit = 25): Promise<WorldRankingEntry[]> {
  return api.get<WorldRankingEntry[]>(`/rankings/world/${worldId}?limit=${limit}`);
}

/** `GET /me/colony`, narrowed to what the Rankings screen needs: which world to rank, and the colony's name for the standing card. */
export function fetchMyWorldId(): Promise<{ id: string; worldId: string; name?: string }> {
  return api.get<{ id: string; worldId: string; name?: string }>('/me/colony');
}
